<script setup>
import { ref } from 'vue'
import { useStore } from 'vuex'
import { Sparkles } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog'

const props = defineProps({
  projectId: { type: Number, required: true },
  triggerLabel: { type: String, default: '' },
  triggerIcon: { type: [Object, Function], default: () => Sparkles },
})

const emit = defineEmits(['generated'])

const store = useStore()

const open = ref(false)
const prompt = ref('')
const generateFields = ref(true)
const generateSummaryPrompt = ref(true)
const isGenerating = ref(false)

const handleOpenChange = (val) => {
  if (isGenerating.value) return
  open.value = val
}

const onGenerate = async () => {
  if (!prompt.value.trim() || isGenerating.value) return
  isGenerating.value = true
  try {
    const draft = await store.dispatch('geo/generateReportTemplate', {
      projectId: props.projectId,
      prompt: prompt.value.trim(),
      generate_fields: generateFields.value,
      generate_summary_prompt: generateSummaryPrompt.value,
    })
    if (draft) {
      emit('generated', draft)
      open.value = false
    }
  } catch (e) {
    // toast already shown by the action
  } finally {
    isGenerating.value = false
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="handleOpenChange">
    <DialogTrigger as-child>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        class="text-muted-foreground"
        data-testid="generate-template-button"
      >
        <component :is="triggerIcon" class="w-4 h-4" :class="triggerLabel ? 'mr-1' : ''" />
        <span v-if="triggerLabel">{{ triggerLabel }}</span>
      </Button>
    </DialogTrigger>
    <DialogContent class="max-w-lg">
      <DialogHeader>
        <DialogTitle>Generate Template</DialogTitle>
        <DialogDescription>
          Describe what data you want to collect.
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4">
        <Textarea
          v-model="prompt"
          :disabled="isGenerating"
          rows="4"
          placeholder="e.g. Report plastic waste collected during an ocean cleanup"
          data-testid="generate-template-prompt"
          @keydown.enter.exact.prevent="onGenerate"
        />

        <div class="flex items-center">
          <Switch id="gen-fields" v-model="generateFields" :disabled="isGenerating" />
          <Label for="gen-fields" class="ml-2">Create data fields</Label>
        </div>

        <div class="flex items-center">
          <Switch id="gen-summary" v-model="generateSummaryPrompt" :disabled="isGenerating" />
          <Label for="gen-summary" class="ml-2">Create summary prompt</Label>
        </div>

        <div class="flex justify-end pt-1">
          <Button
            type="button"
            :disabled="!prompt.trim() || isGenerating"
            @click="onGenerate"
            data-testid="generate-template-submit"
          >
            <Spinner v-if="isGenerating" class="w-4 h-4 mr-2" />
            Generate
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
